import { Box, Button, CircularProgress, Backdrop, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, ToggleButton, ToggleButtonGroup, TextField, InputAdornment, Select, MenuItem, FormControl, IconButton, useTheme, useMediaQuery, Chip } from "@mui/material";
import { WhatsApp, Telegram, Email, Sms, Edit, Delete, Search, Refresh, LocalOffer } from "@mui/icons-material";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import MenuIcon from '@mui/icons-material/Menu';

import { Template } from "modules/Interfaces/InstanUpdates/Template";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Logger } from "utils/Logger";
import { apiClient } from "utils/ApiClient";

import { SystemTemplateDialog } from "./SystemTemplateDialog";
import { SystemEmailRegisterDialog } from "./SystemEmailRegisterDialog";
import { SystemEmailEditDialog } from "./SystemEmailEditDialog";
import { DataTableWithStaticSchema } from "components/DataTable";
import { DynamicBreadcrumbs } from "components/Breadcrumb";
import { generateThumbnailFromText } from "utils/ThumbnailGenerator";

const SYSTEM_TEMPLATE_PREFIX = 'IUST-';

export const SystemTemplates: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState<boolean>(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState<Partial<Template> | null>(null);

    const SYSTEM_TEMPLATES_ACTIVE_TAB_STORAGE_KEY = 'administration-system-templates-active-tab';
    const [activeTab, setActiveTab] = useState<string>(() => {
        try {
            const stored = localStorage.getItem(SYSTEM_TEMPLATES_ACTIVE_TAB_STORAGE_KEY);
            if (
                stored === 'whatsapp' ||
                stored === 'mail' ||
                stored === 'sms' ||
                stored === 'telegram' ||
                stored === 'whatsappbusiness'
            ) {
                return stored;
            }
        } catch {
            /* ignore quota / private mode */
        }
        return 'whatsapp';
    });
    const [registerEmailDialogOpen, setRegisterEmailDialogOpen] = useState(false);
    const [emailEditDialogOpen, setEmailEditDialogOpen] = useState(false);
    const [editingEmailTemplate, setEditingEmailTemplate] = useState<Template | null>(null);

    const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<any>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [templateNameError, setTemplateNameError] = useState('');
    const [saveError, setSaveError] = useState<string>('');
    const [templateDataRefreshKey, setTemplateDataRefreshKey] = useState(0);

    const VIEWMODE_STORAGE_KEY = 'system-templates-view-mode';
    const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
        const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
        return stored === 'table' ? 'table' : 'tile';
    });
    const [tileTemplates, setTileTemplates] = useState<any[]>([]);
    const [tileLoading, setTileLoading] = useState(false);
    const [tileSearchQuery, setTileSearchQuery] = useState('');
    const [tableSearchQuery, setTableSearchQuery] = useState('');
    const [tileSortField, setTileSortField] = useState<'name' | 'lastUpdated'>('name');
    const [tileSortOrder, setTileSortOrder] = useState<'asc' | 'desc'>('asc');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const TILE_PAGE_SIZE = 20;
    const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);

    const CATEGORY_OPTIONS = [
        { value: '', label: 'All Categories' },
        { value: 'transactional', label: 'Transactional' },
        { value: 'promotional', label: 'Promotional' },
        { value: 'service', label: 'Service' },
        { value: 'system', label: 'System' },
    ];

    const TABS: { label: string; value: string; icon: React.ReactElement; gradient: string; hoverGradient: string }[] = [
        { label: 'WhatsApp', value: 'whatsapp', icon: <WhatsApp fontSize="small" />, gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', hoverGradient: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)' },
        { label: 'Email', value: 'mail', icon: <Email fontSize="small" />, gradient: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)', hoverGradient: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)' },
        { label: 'SMS', value: 'sms', icon: <Sms fontSize="small" />, gradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', hoverGradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)' },
        { label: 'Telegram', value: 'telegram', icon: <Telegram fontSize="small" />, gradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', hoverGradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' },
        { label: 'WhatsApp Business', value: 'whatsappbusiness', icon: <WhatsApp fontSize="small" />, gradient: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', hoverGradient: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)' },
    ];

    const fetchTileData = useCallback(async (tabValue: string) => {
        try {
            setTileLoading(true);
            const data = await apiClient.get(`/iu/systemtemplate/type/${tabValue}`);
            setTileTemplates(Array.isArray(data.data) ? data.data : []);
        } catch (error) {
            Logger.error("Failed to fetch system templates for tile view:", error);
            setTileTemplates([]);
        } finally {
            setTileLoading(false);
        }
    }, []);

    useEffect(() => {
        if (viewMode === 'tile') {
            fetchTileData(activeTab);
        }
    }, [viewMode, activeTab, templateDataRefreshKey, fetchTileData]);

    const filteredTileTemplates = useMemo(() => {
        let results = tileTemplates;
        if (tileSearchQuery.trim()) {
            const q = tileSearchQuery.toLowerCase();
            results = results.filter((tmpl: any) => {
                const name = (tmpl.templateName || '').toLowerCase();
                const displayName = name.startsWith('iust-') ? name.substring(5) : name;
                const subject = (tmpl.emailSubject || '').toLowerCase();
                const keywordsMatch = Array.isArray(tmpl.keywords) && tmpl.keywords.some((kw: string) => (kw || '').toLowerCase().includes(q));
                return displayName.includes(q) || name.includes(q) || subject.includes(q) || keywordsMatch;
            });
        }
        if (categoryFilter) {
            results = results.filter((tmpl: any) => (tmpl.category || '').toLowerCase() === categoryFilter.toLowerCase());
        }
        const sorted = [...results].sort((a: any, b: any) => {
            let cmp = 0;
            if (tileSortField === 'name') {
                const nameA = (a.templateName || '').toLowerCase();
                const nameB = (b.templateName || '').toLowerCase();
                cmp = nameA.localeCompare(nameB);
            } else {
                const dateA = new Date(a.templateUpdatedOn || a.updatedAt || 0).getTime();
                const dateB = new Date(b.templateUpdatedOn || b.updatedAt || 0).getTime();
                cmp = dateA - dateB;
            }
            return tileSortOrder === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [tileTemplates, tileSearchQuery, tileSortField, tileSortOrder, categoryFilter]);

    useEffect(() => {
        setTileVisibleCount(TILE_PAGE_SIZE);
    }, [tileSearchQuery, activeTab, categoryFilter]);

    const templateSchema = useMemo(() => ({
        paths: {
            _id: { instance: 'String', isRequired: false },
            thumbnailUrl: { instance: 'String', isRequired: false },
            templateName: { instance: 'String', isRequired: true },
            templateType: { instance: 'String', isRequired: true },
            estimatedCreditCost: { instance: 'Number', isRequired: false },
            templateContent: { instance: 'String', isRequired: true },
            emailSubject: { instance: 'String', isRequired: false },
            senderHeaderName: { instance: 'String', isRequired: false },
            collectionId: { instance: 'String', isRequired: false },
            accessLevel: { instance: 'String', isRequired: true },
            keywords: { instance: 'Array', isRequired: false },
            sharedTo: { instance: 'Array', isRequired: false },
            templateCreatedByUserId: { instance: 'String', isRequired: false },
            templateUpdatedByUserId: { instance: 'String', isRequired: false },
            templateUpdatedOn: { instance: 'Date', isRequired: false },
            createdAt: { instance: 'Date', isRequired: false },
            updatedAt: { instance: 'Date', isRequired: false },
            __v: { instance: 'Number', isRequired: false },
        },
        obj: {}
    }), []);

    const deleteTemplate = useCallback(async (template_id: string) => {
        try {
            setLoading(true);
            await apiClient.delete(`/iu/systemtemplate/${template_id}`);
            setTemplateDataRefreshKey(prev => prev + 1);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            Logger.error("Failed to delete system template:", error);
        }
    }, []);

    const saveTemplate = useCallback(async () => {
        if (!currentTemplate) return;
        try {
            setLoading(true);
            setSaveError('');
            setTemplateNameError('');
            const { _id, companyId, ...payloadToSend } = currentTemplate as any;

            const templateTypeForSave = (payloadToSend.templateType || '').toLowerCase();
            if (templateTypeForSave === 'whatsapp') {
                delete payloadToSend.mediaAttachments;
                delete payloadToSend.deletedMediaAttachments;
            } else if (isEditing && (templateTypeForSave === 'sms' || templateTypeForSave === 'gsmtext')) {
                const sh = payloadToSend.senderHeaderName;
                if (sh === undefined || sh === '') {
                    payloadToSend.senderHeaderName = null;
                }
            } else if (isEditing && payloadToSend.mediaAttachments) {
                const newAttachments = payloadToSend.mediaAttachments.filter(
                    (attachment: any) => attachment.isNew === true
                );
                const deletedAttachments = payloadToSend.mediaAttachments.filter(
                    (attachment: any) => attachment.isDeleted === true
                );
                const orphanedAttachments = payloadToSend.mediaAttachments.filter(
                    (attachment: any) => attachment.isOrphaned === true
                );
                payloadToSend.mediaAttachments = newAttachments.map((attachment: any) => ({
                    type: attachment.type, filename: attachment.filename,
                    mimeType: attachment.mimeType, size: attachment.size,
                    caption: attachment.caption, data: attachment.data
                }));
                payloadToSend.deletedMediaAttachments = [
                    ...deletedAttachments.map((att: any) => att.filename),
                    ...orphanedAttachments.map((att: any) => att.filename)
                ];
            }

            const templateType = (payloadToSend.templateType || '').toLowerCase();
            const textContent = payloadToSend.templateContent || '';
            if (textContent.trim() && ['whatsapp', 'sms', 'telegram', 'whatsappbusiness'].includes(templateType)) {
                const dataUrl = await generateThumbnailFromText(textContent, templateType);
                if (dataUrl) {
                    payloadToSend.thumbnail = { data: dataUrl, mimeType: 'image/png' };
                }
            }

            if (isEditing) {
                await apiClient.put(`/iu/systemtemplate/${currentTemplate._id}`, payloadToSend);
            } else {
                await apiClient.post('/iu/systemtemplate/new', payloadToSend);
            }
            setTemplateDataRefreshKey(prev => prev + 1);
            closeDialog();
            setLoading(false);
        } catch (error) {
            setLoading(false);
            Logger.error("Failed to save system template:", error);
            const msg = error instanceof Error ? error.message : 'Failed to save system template';
            setSaveError(msg);
            if (/already exists in IU-SystemTemplates|IU-SystemTemplates|E11000|duplicate key/i.test(msg)) {
                setTemplateNameError(msg);
            }
        }
    }, [currentTemplate, isEditing]);

    const openWhatsAppDialog = (template?: Template) => {
        setCurrentTemplate(template || {
            templateName: "", templateType: "whatsapp",
            templateContent: "", accessLevel: "public"
        });
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const openTelegramDialog = (template?: Template) => {
        setCurrentTemplate(template || {
            templateName: "", templateType: "telegram",
            templateContent: "", accessLevel: "public"
        });
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const openEmailDialog = (template?: Template) => {
        if (template) {
            setEditingEmailTemplate(template);
            setEmailEditDialogOpen(true);
        } else {
            setRegisterEmailDialogOpen(true);
        }
    };

    const openSMSDialog = (template?: Template) => {
        setCurrentTemplate(template || {
            templateName: "", templateType: "sms",
            templateContent: "", accessLevel: "public"
        });
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const openWhatsAppBusinessDialog = (template?: Template) => {
        setCurrentTemplate(template || {
            templateName: "", templateType: "whatsappbusiness",
            templateContent: "", accessLevel: "public",
            whatsappBusinessConfig: {
                language: 'en_US',
                header: { type: 'none' },
                footer: '',
                buttons: [],
            },
        });
        setIsEditing(!!template);
        setDialogOpen(true);
        setTemplateNameError('');
        setSaveError('');
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setCurrentTemplate(null);
        setTemplateNameError('');
        setSaveError('');
    };

    const handleEmailTemplateSuccess = () => {
        setTemplateDataRefreshKey(prev => prev + 1);
    };

    const [refreshingMetaStatus, setRefreshingMetaStatus] = useState(false);
    const handleRefreshMetaStatuses = async () => {
        try {
            setRefreshingMetaStatus(true);
            await apiClient.post('/iu/systemtemplate/meta/refresh-all');
            setTemplateDataRefreshKey(prev => prev + 1);
        } catch (error) {
            Logger.error("Failed to refresh Meta statuses:", error);
        } finally {
            setRefreshingMetaStatus(false);
        }
    };

    const handleDataTableEdit = (record: any) => {
        if (record.templateType === 'whatsapp') openWhatsAppDialog(record);
        else if (record.templateType === 'whatsappbusiness') openWhatsAppBusinessDialog(record);
        else if (record.templateType === 'telegram') openTelegramDialog(record);
        else if (record.templateType === 'mail') openEmailDialog(record);
        else if (record.templateType === 'sms') openSMSDialog(record);
        else openWhatsAppDialog(record);
    };

    const handleDataTableDelete = (record: any) => {
        setTemplateToDelete(record);
        setDeleteConfirmDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!templateToDelete) return;
        const templateId = typeof templateToDelete === 'string' ? templateToDelete : templateToDelete?._id;
        if (templateId) {
            setDeleteLoading(true);
            await deleteTemplate(templateId);
            setDeleteLoading(false);
        }
        setDeleteConfirmDialogOpen(false);
        setTemplateToDelete(null);
    };

    const handleCancelDelete = () => {
        setDeleteConfirmDialogOpen(false);
        setTemplateToDelete(null);
    };

    const getDisplayTemplateName = useCallback((fullTemplateName: string | undefined) => {
        if (!fullTemplateName) return "";
        if (fullTemplateName.startsWith(SYSTEM_TEMPLATE_PREFIX)) {
            return fullTemplateName.substring(SYSTEM_TEMPLATE_PREFIX.length);
        }
        return fullTemplateName;
    }, []);

    const getTabConfig = useCallback((tabValue: string) => {
        return TABS.find(t => t.value === tabValue) || TABS[0];
    }, []);

    const formatDate = useCallback((dateStr?: string | Date) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
            hour12: false,
        });
    }, []);

    const categoryDataProcessor = useCallback((data: any[]) => {
        if (!categoryFilter) return data;
        return data.filter((row: any) => (row.category || '').toLowerCase() === categoryFilter.toLowerCase());
    }, [categoryFilter]);

    const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

    const templateColumnRenderers = useMemo(() => ({
        thumbnailUrl: (row: any) => {
            const url = row.thumbnailUrl;
            if (!url) return <Typography variant="caption" color="text.secondary">No preview</Typography>;
            return (
                <Box
                    sx={{ cursor: 'pointer', display: 'inline-block' }}
                    onClick={(e) => { e.stopPropagation(); setThumbnailPreview(url); }}
                >
                    <img
                        src={url}
                        alt="Template preview"
                        style={{
                            width: 60,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 4,
                            border: '1px solid rgba(0,0,0,0.12)',
                        }}
                    />
                </Box>
            );
        },
        templateName: (row: Template) => getDisplayTemplateName(row.templateName),
        estimatedCreditCost: (row: any) => {
            const v = row.estimatedCreditCost;
            if (typeof v !== 'number' || Number.isNaN(v)) {
                return <Typography variant="caption" color="text.secondary">—</Typography>;
            }
            return (
                <Chip
                    icon={<LocalOffer sx={{ fontSize: 18 }} />}
                    label={v}
                    size="small"
                    sx={{
                        fontWeight: 800,
                        color: 'success.main',
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.96)',
                        border: '1px solid',
                        borderColor: 'success.main',
                        height: 26,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                        '& .MuiChip-icon': { ml: 0.5, color: 'success.main' },
                        '& .MuiChip-label': { px: 0.75, fontSize: '0.75rem' },
                    }}
                />
            );
        },
        templateUpdatedOn: (row: Template) => {
            if (row.templateUpdatedOn) {
                const date = new Date(row.templateUpdatedOn);
                return date.toLocaleString('en-GB', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                    timeZoneName: 'short', hour12: false,
                });
            }
            return '';
        },
        templateType: (row: Template) => {
            const templateType = row.templateType?.toLowerCase() || '';
            let buttonProps = { background: '', hoverBackground: '', color: 'primary' as any, text: templateType.toUpperCase() };
            switch (templateType) {
                case 'whatsapp':
                    buttonProps = { background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', hoverBackground: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)', color: 'success', text: 'WHATSAPP' }; break;
                case 'telegram':
                    buttonProps = { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', hoverBackground: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'info', text: 'TELEGRAM' }; break;
                case 'mail':
                    buttonProps = { background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)', hoverBackground: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)', color: 'warning', text: 'EMAIL' }; break;
                case 'sms':
                    buttonProps = { background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', hoverBackground: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', color: 'secondary', text: 'SMS' }; break;
                case 'whatsappbusiness':
                    buttonProps = { background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', hoverBackground: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', color: 'success', text: 'WA BUSINESS' }; break;
                default:
                    buttonProps = { background: 'linear-gradient(135deg, #757575 0%, #616161 100%)', hoverBackground: 'linear-gradient(135deg, #757575 0%, #616161 100%)', color: 'default', text: templateType.toUpperCase() };
            }
            return (
                <Button variant="contained" color={buttonProps.color}
                    sx={{
                        background: buttonProps.background, color: 'common.white',
                        padding: "2px 8px", fontSize: "11px", boxShadow: "none",
                        textTransform: "none", minWidth: "auto", height: "24px", fontWeight: "bold",
                        '&:hover': { background: buttonProps.hoverBackground }
                    }}>
                    {buttonProps.text}
                </Button>
            );
        },
        accessLevel: (row: Template) => {
            const accessLevel = row.accessLevel?.toLowerCase() || '';
            return (
                <Button variant="contained" color={accessLevel === 'private' ? 'success' : 'error'}
                    sx={{ padding: "2px 8px", fontSize: "11px", boxShadow: "none", textTransform: "capitalize", minWidth: "auto", height: "24px" }}>
                    {accessLevel}
                </Button>
            );
        },
        metaStatus: (row: any) => {
            if (!row.metaStatus) return <Typography variant="caption" color="text.secondary">—</Typography>;
            const colorMap: Record<string, 'success' | 'error' | 'warning' | 'info'> = { APPROVED: 'success', REJECTED: 'error', PENDING: 'warning', PAUSED: 'info', DISABLED: 'error' };
            return <Chip label={row.metaStatus} size="small" color={colorMap[row.metaStatus] || 'default'} sx={{ fontSize: '0.7rem', fontWeight: 700, height: 22 }} />;
        },
        keywords: (row: any) => {
            const kws: string[] = Array.isArray(row.keywords) ? row.keywords : [];
            if (kws.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
            return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {kws.map((kw, i) => (
                        <Chip key={i} label={kw} size="small" variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22 }} />
                    ))}
                </Box>
            );
        },
    }), [getDisplayTemplateName]);


    return (loading ?
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '256px' }}>
            <Backdrop
                sx={(theme: import('@mui/material/styles').Theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
                open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
        :
        <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs items={[
                { itemPath: "/dashboard", itemLabel: "Administration" },
                { itemPath: "/administration/system-templates", itemLabel: "System Templates" }
            ]} />
            <Box sx={{ mb: 0, px: 0 }}>
                <Tabs value={activeTab} onChange={(_e, v) => {
                    setActiveTab(v);
                    setTileSearchQuery('');
                    setCategoryFilter('');
                    try {
                        localStorage.setItem(SYSTEM_TEMPLATES_ACTIVE_TAB_STORAGE_KEY, v);
                    } catch {
                        /* ignore quota / private mode */
                    }
                }}
                    variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                    sx={{
                        minHeight: 0, p: 0, m: 0,
                        '& .MuiTabs-scroller': { p: 0, m: 0 },
                        '& .MuiTabs-flexContainer': { gap: 0, p: 0, m: 0 },
                        '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
                        '& .MuiTabs-indicator': { backgroundColor: 'secondary.main' }
                    }}>
                    {TABS.map(tab => (
                        <Tab key={tab.value} value={tab.value} iconPosition="start" icon={tab.icon} label={tab.label}
                            sx={{
                                textTransform: 'none', fontWeight: 600, borderRadius: 1,
                                border: '1px solid', borderColor: 'divider', mr: 0.5, px: 1.5, minHeight: 40,
                                color: activeTab === tab.value ? 'common.white' : 'text.primary',
                                background: activeTab === tab.value ? tab.gradient : 'transparent',
                                '&.Mui-selected': { color: 'common.white' },
                                '&:hover': { background: activeTab === tab.value ? tab.hoverGradient : 'action.hover' },
                                '&:first-of-type': { ml: 0 }
                            }} />
                    ))}
                </Tabs>
            </Box>

            {TABS.map((tab) => (
                activeTab === tab.value ? (
                    <Box key={`panel-${tab.value}`}>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, backgroundColor: 'background.paper', mb: 2 }}>
                            {/* Mobile: full-width New Template button */}
                            <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 1.5 }}>
                                {tab.value === 'whatsapp' ? (
                                    <Button variant="contained" color="success" startIcon={<WhatsApp />}
                                        onClick={() => openWhatsAppDialog()} fullWidth
                                        sx={{
                                            background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                                            '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)' },
                                            fontWeight: 700, py: 1.2,
                                            border: 2, borderColor: 'secondary.main',
                                            boxShadow: '0 3px 10px rgba(46, 125, 50, 0.35)',
                                            animation: 'glowSoft-green 2.5s ease-in-out infinite',
                                            '@keyframes glowSoft-green': {
                                                '0%, 100%': { boxShadow: '0 3px 10px rgba(46, 125, 50, 0.35), 0 0 6px rgba(46, 125, 50, 0.2)' },
                                                '50%': { boxShadow: '0 3px 14px rgba(46, 125, 50, 0.55), 0 0 18px rgba(46, 125, 50, 0.35)' },
                                            },
                                        }}>
                                        New
                                    </Button>
                                ) : tab.value === 'mail' ? (
                                    <Button variant="contained" color="warning" startIcon={<Email />}
                                        onClick={() => openEmailDialog()} fullWidth
                                        sx={{
                                            background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)',
                                            '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)' },
                                            fontWeight: 700, py: 1.2,
                                            border: 2, borderColor: 'secondary.main',
                                            boxShadow: '0 3px 10px rgba(237, 108, 2, 0.35)',
                                            animation: 'glowSoft-orange 2.5s ease-in-out infinite',
                                            '@keyframes glowSoft-orange': {
                                                '0%, 100%': { boxShadow: '0 3px 10px rgba(237, 108, 2, 0.35), 0 0 6px rgba(237, 108, 2, 0.2)' },
                                                '50%': { boxShadow: '0 3px 14px rgba(237, 108, 2, 0.55), 0 0 18px rgba(237, 108, 2, 0.35)' },
                                            },
                                        }}>
                                        New
                                    </Button>
                                ) : tab.value === 'telegram' ? (
                                    <Button variant="contained" color="info" startIcon={<Telegram />}
                                        onClick={() => openTelegramDialog()} fullWidth
                                        sx={{
                                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                            '&:hover': { background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' },
                                            fontWeight: 700, py: 1.2,
                                            border: 2, borderColor: 'secondary.main',
                                        }}>
                                        New
                                    </Button>
                                ) : tab.value === 'sms' ? (
                                    <Button variant="contained" color="secondary" startIcon={<Sms />}
                                        onClick={() => openSMSDialog()} fullWidth
                                        sx={{
                                            background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                            color: 'common.white',
                                            '&:hover': { background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)' },
                                            fontWeight: 700, py: 1.2,
                                            border: 2, borderColor: 'secondary.main',
                                            boxShadow: '0 3px 10px rgba(156, 39, 176, 0.35)',
                                            animation: 'glowSoft-purple 2.5s ease-in-out infinite',
                                            '@keyframes glowSoft-purple': {
                                                '0%, 100%': { boxShadow: '0 3px 10px rgba(156, 39, 176, 0.35), 0 0 6px rgba(156, 39, 176, 0.2)' },
                                                '50%': { boxShadow: '0 3px 14px rgba(156, 39, 176, 0.55), 0 0 18px rgba(156, 39, 176, 0.35)' },
                                            },
                                        }}>
                                        New
                                    </Button>
                                ) : tab.value === 'whatsappbusiness' ? (
                                    <Button variant="contained" color="success" startIcon={<WhatsApp />}
                                        onClick={() => openWhatsAppBusinessDialog()} fullWidth
                                        sx={{
                                            background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                                            '&:hover': { background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)' },
                                            fontWeight: 700, py: 1.2,
                                            border: 2, borderColor: 'secondary.main',
                                            boxShadow: '0 3px 10px rgba(37, 211, 102, 0.35)',
                                            animation: 'glowSoft-wabiz 2.5s ease-in-out infinite',
                                            '@keyframes glowSoft-wabiz': {
                                                '0%, 100%': { boxShadow: '0 3px 10px rgba(37, 211, 102, 0.35), 0 0 6px rgba(37, 211, 102, 0.2)' },
                                                '50%': { boxShadow: '0 3px 14px rgba(37, 211, 102, 0.55), 0 0 18px rgba(37, 211, 102, 0.35)' },
                                            },
                                        }}>
                                        New
                                    </Button>
                                ) : null}
                            </Box>

                            {/* Toolbar: Search | Category | Sort by, Order by, View toggle, New button */}
                            <Box sx={{
                                mb: 1.5, p: 1.5, borderRadius: 2,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                            }}>
                                <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
                                    <TextField
                                        size="small"
                                        placeholder={viewMode === 'tile' ? 'Search templates...' : 'Search across all columns...'}
                                        value={viewMode === 'tile' ? tileSearchQuery : tableSearchQuery}
                                        onChange={(e) => viewMode === 'tile' ? setTileSearchQuery(e.target.value) : setTableSearchQuery(e.target.value)}
                                        sx={{ flexGrow: 1, minWidth: 0, maxWidth: { xs: '100%', sm: 240 } }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search fontSize="small" color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 140 }, flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}>
                                        <Select
                                            value={categoryFilter}
                                            onChange={(e) => setCategoryFilter(e.target.value)}
                                            displayEmpty
                                            sx={{ fontSize: '0.82rem' }}
                                        >
                                            <MenuItem value="">Category: All</MenuItem>
                                            {activeTab === 'whatsappbusiness' ? [
                                                <MenuItem key="MARKETING" value="MARKETING">Category: Marketing</MenuItem>,
                                                <MenuItem key="UTILITY" value="UTILITY">Category: Utility</MenuItem>,
                                                <MenuItem key="AUTHENTICATION" value="AUTHENTICATION">Category: Authentication</MenuItem>,
                                            ] : [
                                                <MenuItem key="transactional" value="transactional">Category: Transactional</MenuItem>,
                                                <MenuItem key="promotional" value="promotional">Category: Promotional</MenuItem>,
                                                <MenuItem key="service" value="service">Category: Service</MenuItem>,
                                                <MenuItem key="system" value="system">Category: System</MenuItem>,
                                            ]}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{ flex: { xs: '0 0 auto', sm: 1 }, display: { xs: 'none', sm: 'block' } }} />
                                {viewMode === 'tile' && (
                                    <>
                                    <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 130 }, flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}>
                                        <Select
                                            value={tileSortField}
                                            onChange={(e) => setTileSortField(e.target.value as 'name' | 'lastUpdated')}
                                            displayEmpty
                                            sx={{ fontSize: '0.82rem' }}
                                        >
                                            <MenuItem value="name">Sort by: Name</MenuItem>
                                            <MenuItem value="lastUpdated">Sort by: Last Updated</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: { xs: 90, sm: 110 }, flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}>
                                        <Select
                                            value={tileSortOrder}
                                            onChange={(e) => setTileSortOrder(e.target.value as 'asc' | 'desc')}
                                            displayEmpty
                                            sx={{ fontSize: '0.82rem' }}
                                        >
                                            <MenuItem value="asc">ASC</MenuItem>
                                            <MenuItem value="desc">DESC</MenuItem>
                                        </Select>
                                    </FormControl>
                                    </>
                                )}
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(_e, newMode) => {
                                        if (newMode !== null) {
                                            setViewMode(newMode);
                                            localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        display: { xs: 'none', sm: 'inline-flex' },
                                        '& .MuiToggleButton-root': {
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '&.Mui-selected': {
                                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#191f45' : 'secondary.main',
                                                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'secondary.contrastText',
                                                '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#232a54' : 'secondary.dark' }
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value="tile" aria-label="Tile view">
                                        <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="table" aria-label="Table view">
                                        <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {tab.value === 'whatsapp' ? (
                                    <Button variant="contained" color="success" startIcon={<WhatsApp />}
                                        onClick={() => openWhatsAppDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                ) : tab.value === 'mail' ? (
                                    <Button variant="contained" color="warning" startIcon={<Email />}
                                        onClick={() => openEmailDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #ed6c02 0%, #e65100 100%)', '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ed6c02 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                ) : tab.value === 'telegram' ? (
                                    <Button variant="contained" color="info" startIcon={<Telegram />}
                                        onClick={() => openTelegramDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', '&:hover': { background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                ) : tab.value === 'sms' ? (
                                    <Button variant="contained" color="secondary" startIcon={<Sms />}
                                        onClick={() => openSMSDialog()} size="small"
                                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', color: 'common.white', '&:hover': { background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)' }, whiteSpace: 'nowrap' }}>
                                        New
                                    </Button>
                                ) : tab.value === 'whatsappbusiness' ? (
                                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, alignItems: 'center' }}>
                                        <IconButton size="small" onClick={handleRefreshMetaStatuses} disabled={refreshingMetaStatus}
                                            sx={{ border: '1px solid', borderColor: '#25d366', color: '#25d366', '&:hover': { borderColor: '#128c7e', backgroundColor: 'rgba(37,211,102,0.06)' } }}>
                                            {refreshingMetaStatus ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
                                        </IconButton>
                                        <Button variant="contained" color="success" startIcon={<WhatsApp />}
                                            onClick={() => openWhatsAppBusinessDialog()} size="small"
                                            sx={{ background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', '&:hover': { background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)' }, whiteSpace: 'nowrap' }}>
                                            New
                                        </Button>
                                    </Box>
                                ) : null}
                                <IconButton
                                    sx={{ display: { xs: 'flex', sm: 'none' }, ml: 'auto', border: '1px solid', borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }}
                                    onClick={() => setToolbarMenuOpen(true)}
                                    size="small"
                                >
                                    <MenuIcon />
                                </IconButton>
                            </Box>
                            </Box>

                            {/* Table View */}
                            {viewMode === 'table' && (
                                <DataTableWithStaticSchema
                                    dataUrl={`/iu/systemtemplate/type/${tab.value}`}
                                    schemaObject={templateSchema}
                                    columnsToExclude={['_id', '__v', 'templateCreatedByUserId', 'templateUpdatedByUserId', 'createdAt', 'updatedAt', 'sharedTo', 'templateContent', 'emailSubject', 'collectionId']}
                                    columnsWithDisabledFilter={['thumbnailUrl']}
                                    onEdit={handleDataTableEdit}
                                    onDelete={handleDataTableDelete}
                                    columnRenderers={templateColumnRenderers}
                                    refreshTrigger={templateDataRefreshKey}
                                    customDataProcessor={categoryDataProcessor}
                                    hideSearch
                                    externalSearchQuery={tableSearchQuery}
                                />
                            )}

                            {/* Tile View */}
                            {viewMode === 'tile' && (
                                tileLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                        <CircularProgress color="secondary" />
                                    </Box>
                                ) : (
                                    <Box>
                                        {filteredTileTemplates.length === 0 ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {tileTemplates.length === 0 ? 'No templates found.' : 'No templates match your search.'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                    <>
                                    <Box display="flex" flexWrap="wrap" gap={2.5} justifyContent="normal">
                                        {filteredTileTemplates.slice(0, tileVisibleCount).map((tmpl: any) => {
                                            const tabConf = getTabConfig(tmpl.templateType);
                                            return (
                                                <Box
                                                    key={tmpl._id}
                                                    sx={{
                                                        width: 260,
                                                        height: 200,
                                                        borderRadius: 2.5,
                                                        overflow: 'hidden',
                                                        position: 'relative',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                        cursor: 'pointer',
                                                        flexShrink: 0,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        '&:hover .tile-overlay': {
                                                            height: 136,
                                                        },
                                                        '&:hover .tile-details': {
                                                            opacity: 1,
                                                        },
                                                        '@media (hover: none)': {
                                                            '& .tile-overlay': { height: 136 },
                                                            '& .tile-details': { opacity: 1 },
                                                        },
                                                    }}
                                                >
                                                    {['sms', 'gsmtext'].includes(String(tmpl.templateType || '').toLowerCase()) &&
                                                        typeof tmpl.estimatedCreditCost === 'number' && (
                                                        <Chip
                                                            icon={<LocalOffer sx={{ fontSize: 18 }} />}
                                                            label={tmpl.estimatedCreditCost}
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                left: 8,
                                                                zIndex: 2,
                                                                fontWeight: 800,
                                                                color: 'success.main',
                                                                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.96)',
                                                                border: '1px solid',
                                                                borderColor: 'success.main',
                                                                height: 28,
                                                                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                                                '& .MuiChip-icon': { ml: 0.5, color: 'success.main' },
                                                                '& .MuiChip-label': { px: 0.75, fontSize: '0.8rem' },
                                                            }}
                                                        />
                                                    )}
                                                    {/* Thumbnail background - covers entire tile */}
                                                    {tmpl.thumbnailUrl ? (
                                                        <img
                                                            src={tmpl.thumbnailUrl}
                                                            alt=""
                                                            onClick={(e) => { e.stopPropagation(); setThumbnailPreview(tmpl.thumbnailUrl); }}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0, left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                objectPosition: 'top',
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0, left: 0, right: 0, bottom: 0,
                                                                background: tabConf.gradient,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                opacity: 0.18,
                                                            }}
                                                        >
                                                            {React.cloneElement(tabConf.icon, { sx: { fontSize: 64, color: '#fff' } })}
                                                        </Box>
                                                    )}

                                                    {/* Dark overlay panel - anchored at bottom, grows upward on hover */}
                                                    <Box
                                                        className="tile-overlay"
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: 0, left: 0, right: 0,
                                                            overflow: 'hidden',
                                                            background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                                                            transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            height: 40,
                                                            px: 1.5,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'flex-start',
                                                        }}
                                                    >
                                                        {/* Template name - always visible, first element */}
                                                        <Typography
                                                            variant="subtitle2"
                                                            noWrap
                                                            sx={{
                                                                color: '#fff',
                                                                fontWeight: 700,
                                                                fontSize: '0.82rem',
                                                                lineHeight: '40px',
                                                                textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {getDisplayTemplateName(tmpl.templateName)}
                                                        </Typography>

                                                        {/* Hover-only details: type, access, date, buttons */}
                                                        <Box
                                                            className="tile-details"
                                                            sx={{
                                                                opacity: 0,
                                                                transition: 'opacity 0.3s ease 0.08s',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 0.5,
                                                                pt: 0.25,
                                                                pb: 1,
                                                                flex: 1,
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    sx={{
                                                                        background: tabConf.gradient,
                                                                        color: '#fff',
                                                                        padding: '1px 8px', fontSize: '10px', boxShadow: 'none',
                                                                        textTransform: 'none', minWidth: 'auto', height: 20, fontWeight: 'bold',
                                                                        '&:hover': { background: tabConf.hoverGradient },
                                                                        pointerEvents: 'none',
                                                                    }}
                                                                >
                                                                    {tab.label.toUpperCase()}
                                                                </Button>
                                                                <Button
                                                                    variant="contained"
                                                                    color={tmpl.accessLevel === 'private' ? 'success' : 'error'}
                                                                    size="small"
                                                                    sx={{
                                                                        padding: '1px 8px', fontSize: '10px', boxShadow: 'none',
                                                                        textTransform: 'capitalize', minWidth: 'auto', height: 20,
                                                                        pointerEvents: 'none',
                                                                    }}
                                                                >
                                                                    {tmpl.accessLevel}
                                                                </Button>
                                                                {tmpl.metaStatus && (
                                                                    <Chip label={tmpl.metaStatus} size="small"
                                                                        color={tmpl.metaStatus === 'APPROVED' ? 'success' : tmpl.metaStatus === 'REJECTED' ? 'error' : 'warning'}
                                                                        sx={{ fontSize: '10px', height: 20, fontWeight: 700 }} />
                                                                )}
                                                            </Box>
                                                            {tmpl.templateUpdatedOn && (
                                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                                                                    Updated: {formatDate(tmpl.templateUpdatedOn)}
                                                                </Typography>
                                                            )}
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto' }}>
                                                                <Tooltip title="Edit">
                                                                    <Button
                                                                        color="secondary"
                                                                        variant="contained"
                                                                        size="small"
                                                                        onClick={(e) => { e.stopPropagation(); handleDataTableEdit(tmpl); }}
                                                                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                                                                    >
                                                                        <Edit fontSize="small" />
                                                                    </Button>
                                                                </Tooltip>
                                                                <Tooltip title="Delete">
                                                                    <Button
                                                                        color="error"
                                                                        variant="contained"
                                                                        size="small"
                                                                        onClick={(e) => { e.stopPropagation(); handleDataTableDelete(tmpl); }}
                                                                        sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </Button>
                                                                </Tooltip>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    {tileVisibleCount < filteredTileTemplates.length && (
                                        <Box display="flex" justifyContent="center" mt={3}>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() => setTileVisibleCount(prev => prev + TILE_PAGE_SIZE)}
                                            >
                                                Load More ({filteredTileTemplates.length - tileVisibleCount} remaining)
                                            </Button>
                                        </Box>
                                    )}
                                    </>
                                        )}
                                    </Box>
                                )
                            )}
                        </Box>
                    </Box>
                ) : null
            ))}

            <SystemTemplateDialog
                dialogOpen={dialogOpen}
                closeDialog={closeDialog}
                isEditing={isEditing}
                currentTemplate={currentTemplate}
                setCurrentTemplate={setCurrentTemplate}
                saveTemplate={saveTemplate}
                templateSchema={templateSchema}
                templateNameError={templateNameError}
                setTemplateNameError={setTemplateNameError}
                saveError={saveError}
                setSaveError={setSaveError}
            />

            <SystemEmailRegisterDialog
                open={registerEmailDialogOpen}
                onClose={() => setRegisterEmailDialogOpen(false)}
                onSuccess={handleEmailTemplateSuccess}
            />

            <SystemEmailEditDialog
                open={emailEditDialogOpen}
                onClose={() => { setEmailEditDialogOpen(false); setEditingEmailTemplate(null); }}
                template={editingEmailTemplate}
                onSuccess={handleEmailTemplateSuccess}
            />

            {/* Mobile Toolbar Dialog */}
            <Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Toolbar</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl size="small" fullWidth>
                            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} displayEmpty sx={{ fontSize: '0.82rem' }}>
                                <MenuItem value="">Category: All</MenuItem>
                                {activeTab === 'whatsappbusiness' ? [
                                    <MenuItem key="MARKETING" value="MARKETING">Category: Marketing</MenuItem>,
                                    <MenuItem key="UTILITY" value="UTILITY">Category: Utility</MenuItem>,
                                    <MenuItem key="AUTHENTICATION" value="AUTHENTICATION">Category: Authentication</MenuItem>,
                                ] : [
                                    <MenuItem key="transactional" value="transactional">Category: Transactional</MenuItem>,
                                    <MenuItem key="promotional" value="promotional">Category: Promotional</MenuItem>,
                                    <MenuItem key="service" value="service">Category: Service</MenuItem>,
                                    <MenuItem key="system" value="system">Category: System</MenuItem>,
                                ]}
                            </Select>
                        </FormControl>
                        {viewMode === 'tile' && (
                            <>
                                <FormControl size="small" fullWidth>
                                    <Select value={tileSortField} onChange={(e) => setTileSortField(e.target.value as 'name' | 'lastUpdated')} displayEmpty sx={{ fontSize: '0.82rem' }}>
                                        <MenuItem value="name">Sort by: Name</MenuItem>
                                        <MenuItem value="lastUpdated">Sort by: Last Updated</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                    <Select value={tileSortOrder} onChange={(e) => setTileSortOrder(e.target.value as 'asc' | 'desc')} displayEmpty sx={{ fontSize: '0.82rem' }}>
                                        <MenuItem value="asc">ASC</MenuItem>
                                        <MenuItem value="desc">DESC</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        )}
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ mr: 1 }}>View:</Typography>
                            <ToggleButtonGroup value={viewMode} exclusive onChange={(_e, newMode) => { if (newMode !== null) { setViewMode(newMode); localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode); } }} size="small"
                                sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider', '&.Mui-selected': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#191f45' : 'secondary.main', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'secondary.contrastText', '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#232a54' : 'secondary.dark' } } } }}>
                                <ToggleButton value="tile"><ViewModuleIcon fontSize="small" /></ToggleButton>
                                <ToggleButton value="table"><TableRowsIcon fontSize="small" /></ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToolbarMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Thumbnail Preview Dialog */}
            <Dialog
                open={!!thumbnailPreview}
                onClose={() => setThumbnailPreview(null)}
                maxWidth="md"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 2 }}>
                    <Typography variant="subtitle2" component="span">Template Preview</Typography>
                    <Button variant="outlined" color="secondary" size="small" onClick={() => setThumbnailPreview(null)}
                        sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}>
                        Close
                    </Button>
                </DialogTitle>
                <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center', backgroundColor: 'background.default' }}>
                    {thumbnailPreview && (
                        <img
                            src={thumbnailPreview}
                            alt="Template preview"
                            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 4 }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={deleteConfirmDialogOpen} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', fontWeight: 600 }}>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                        <strong>Delete operation can't be recovered.</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete this system template?
                    </Typography>
                    {templateToDelete && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Template: {getDisplayTemplateName(templateToDelete?.templateName)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Type: {templateToDelete?.templateType?.toUpperCase()}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCancelDelete} variant="outlined" color="inherit" disabled={deleteLoading}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : null}>
                        {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SystemTemplates;
